import { Product } from "../global/components/stock-category-types";

export type SimilarStocksResponse = {
    data: { collection: SimilerStock[] };
};

export interface SimilerStock {
    logo: string;
    symbol: string;
    company_name: string;
    current_price: number;
    price_movement: {
        one_day: number;
        one_day_percentage: number;
    };
    analyst_rating: {
        buy_sell: string;
        percentage_difference: number;
    };
    market_cap: number;
    five_y_returns: number;
    pe_ratio: number;
}

export type ProductPriceResponse = {
    data: ProductPrice | null;
};

export interface ProductPrice {
    symbol: string;
    price: number;
    previous_close: number;
    one_day: number;
    one_day_percentage: number;
    open: number;
    days_low: number;
    days_high: number;
}

// Common types
export interface QuarterIncome {
    quarter: "Q1" | "Q2" | "Q3" | "Q4";
    revenue: string;
    profit: string;
    year: number;
}

export interface QuarterBalanceSheet {
    quarter: "Q1" | "Q2" | "Q3" | "Q4";
    assets: string;
    liabilities: string;
    year: number;
}

export interface IncomeAnnual {
    year: number;
    revenue: string;
    profit: string;
}

// Yearly groupings
export interface IncomeByYear {
    annual: IncomeAnnual[];
    year: number;
    quarters: QuarterIncome[];
}

export interface BalanceSheetAnnual {
    year: number;
    assets: string;
    liabilities: string;
}

export interface BalanceSheetByYear {
    annual: BalanceSheetAnnual[];
    year: number;
    quarters: QuarterBalanceSheet[];
}

// Returns
export interface CompanyReturn {
    period: string; // e.g. "1 month", "3 years"
    percentage: number;
    is_positive: boolean;
}

// Root object
export interface CompanyFinancials {
    income: IncomeByYear;
    balance_sheet: BalanceSheetByYear;
    returns: CompanyReturn[];
}

export const period = {
    NOW: "now",
    "1M": "1M",
    "3M": "3M",
    "6M": "6M",
    "1Y": "1Y",
    "3Y": "3Y",
} as const;

export type Period = (typeof period)[keyof typeof period];

export const periodMappings: { key: string; label: string; textFull: string }[] = [
    { key: "1W", label: "1 W", textFull: "1 Week" },
    { key: "1M", label: "1 M", textFull: "1 Month" },
    { key: "3M", label: "3 M", textFull: "3 Months" },
    { key: "6M", label: "6 M", textFull: "6 Months" },
    { key: "1Y", label: "1 Y", textFull: "1 Year" },
    { key: "3Y", label: "3 Y", textFull: "3 Years" },
    { key: "5Y", label: "5 Y", textFull: "5 Years" },
] as const;

export interface ForecastData {
    target_low: number;
    target_median: number;
    target_high: number;
    no_of_analyst: number;
}
export interface UsStock {
    company_logo: string;
    symbol: string;
    company_name: string;
    company_profile: {
        description: string;
        sector: string;
    };
    company_fundamentals: {
        market_cap: number;
        volume: number;
        pe_ratio: number;
        eps: number;
        dividend_yield: number;
        low_52_week: number;
        high_52_week: number;
        market_cap_category: string;
    };
    live_price: number;
    price_movement: {
        one_day: number;
        one_day_percentage: number;
        days_low: number;
        days_high: number;
        open?: number;
        previous_close?: number;
    };
    exchange: string;
    sector: string;
    country: string;
    category?: string;
    similar_stocks: SimilerStock[];
    company_financials: CompanyFinancials;
    analyst_rating: [
        {
            values: {
                buy: number;
                hold: number;
                sell: number;
                strong_buy: number;
                strong_sell: number;
            };
            label: Period;
        },
    ];
    forecast: ForecastData;
    ibkr_exchange?: string;
}

export type UsStockListApiResponse = {
    collection: UsStock[];
    total_count: number;
    page: number;
    per_page: number;
} | null;

export type ProductScannerApiResponse = {
    collection: UsStock[];
    total_count: number;
    page: number;
    per_page: number;
} | null;

export type UsStockCategory = {
    index: number;
    name: string;
    image: string;
    tagline: string;
    product_count: number;
};

export type ProductCategoryListApiResponse = {
    success: boolean;
    message: string;
    data: {
        categories: UsStockCategory[];
    };
} | null;

export type ProductCategoryDetailsApiResponse = {
    success: boolean;
    message: string;
    data: {
        products: Product[];
    };
} | null;

export type UsStockDetailsApiResponse = {
    success: boolean;
    message: string;
    collection: UsStock;
} | null;

export type NewsAndEventsResponse = {
    data: NewsAndEventsData;
};

export type NewsAndEventsData = {
    company_logo: string;
    company_name: string;
    exchange: string;
    news: NewsAndEvents[];
    pagination: {
        page: number;
        per_page: number;
        total_count: number;
    };
    symbol: string;
};

export type NewsAndEvents = {
    category: string;
    datetime: string;
    headline: string;
    id: string;
    image: string;
    lang: string;
    related: string;
    source: string;
    summary: string;
    url: string;
};

/** chart constants & types */
export const chartTypes = {
    PRICE: "PRICE",
    FORECAST: "FORECAST",
} as const;

export type ChartType = keyof typeof chartTypes;

export const priceGraphPeriod = {
    "1D": "1D",
    "1W": "1W",
    "1M": "1M",
    "6M": "6M",
    "1Y": "1Y",
    "3Y": "3Y",
    "5Y": "5Y",
    ALL: "ALL",
    "--": "--",
} as const;

export type PeriodType = keyof typeof priceGraphPeriod;

export const durationConfig = {
    [priceGraphPeriod["1D"]]: { days: 1, resolution: "1" },
    [priceGraphPeriod["1W"]]: { days: 7, resolution: "5" },
    [priceGraphPeriod["1M"]]: { months: 1, resolution: "D" },
    [priceGraphPeriod["6M"]]: { months: 6, resolution: "D" },
    [priceGraphPeriod["1Y"]]: { years: 1, resolution: "D" },
    [priceGraphPeriod["3Y"]]: { years: 3, resolution: "D" },
    [priceGraphPeriod["5Y"]]: { years: 5, resolution: "D" },
    [priceGraphPeriod["ALL"]]: { all: true, resolution: "D" },
    [priceGraphPeriod["--"]]: { "--": false, resolution: "" },
} as const;

export type ChartData = {
    x: number;
    y: number;
};
export interface PriceData {
    t: number;
    c: number;
}
export interface MonthlyPrice {
    x: Date;
    y: number;
}

export const FUNDAMENTAL_TABS = {
    FUNDAMENTALS: "Fundamentals",
    KEY_FINANCIALS: "Key financials",
    NEWS_AND_EVENTS: "News & events",
    SIMILAR_STOCKS: "Similar stocks",
    // FAQS: "FAQs",
} as const;
export type FundamentalTabValue = (typeof FUNDAMENTAL_TABS)[keyof typeof FUNDAMENTAL_TABS];
